import { zodResolver } from '@hookform/resolvers/zod';
import { AddAPhoto, CheckCircle } from '@mui/icons-material';
import { Box, CircularProgress, Grid2, Typography } from '@mui/material';
import { useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import type { z } from 'zod';
import ButtonComponent from 'components/ButtonComponent';
import ModalComponent from 'components/ModalComponent';
import TextFieldComponent from 'components/TextFieldComponent';
import WebCamCaptureComponent from 'components/WebCamComponent/WebCamCaptureComponent';
import { useGetPublicInviteInfoQuery, useSubmitPublicInviteAsync } from 'services/ResponsibleInvite/hooks/usePublic';
import { PublicResponsibleInviteFormSchema } from 'services/ResponsibleInvite/schemas';
import { convertBase64ToBlob } from 'utils/TransformUtils';

type PublicResponsibleInviteFormInput = z.infer<typeof PublicResponsibleInviteFormSchema>;

const ONLY_DIGITS_REGEX = /\D/g;

const PageShell = ({ children }: { children: React.ReactNode }) => (
  <Box
    sx={{
      minHeight: '100vh',
      width: '100%',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'flex-start',
      py: { xs: 4, md: 8 },
      px: 2,
      backgroundColor: 'var(--app-bg, #101418)',
    }}
  >
    <Box
      sx={{
        width: '100%',
        maxWidth: 480,
        backgroundColor: 'var(--app-surface, #1b2027)',
        borderRadius: 2,
        p: { xs: 3, md: 4 },
        boxShadow: 4,
      }}
    >
      {children}
    </Box>
  </Box>
);

const ResponsibleInvitePage = () => {
  const { token } = useParams<{ token: string }>();
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [photo, setPhoto] = useState<Blob | File | null>(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [photoError, setPhotoError] = useState<string | null>(null);

  const { data: inviteInfo, isLoading, isError } = useGetPublicInviteInfoQuery(token);
  const { mutateAsync: submitInvite, isLoading: isSubmitting } = useSubmitPublicInviteAsync();

  const { control, handleSubmit } = useForm<PublicResponsibleInviteFormInput>({
    resolver: zodResolver(PublicResponsibleInviteFormSchema),
    defaultValues: { name: '', cpf: '', phone: '', email: '' },
  });

  const photoPreviewUrl = useMemo(() => {
    if (!photo) return '';
    return URL.createObjectURL(photo);
  }, [photo]);

  const onCapture = (imageBase64: string | null) => {
    if (!imageBase64) return;
    setPhoto(convertBase64ToBlob(imageBase64, 'image/jpeg'));
    setPhotoError(null);
    setIsCameraOpen(false);
  };

  const onChangeSelectedImage = (file: File) => {
    setPhoto(file);
    setPhotoError(null);
    setIsCameraOpen(false);
  };

  const handleSave = async (data: PublicResponsibleInviteFormInput) => {
    if (!token) return;

    if (!photo) {
      setPhotoError('É necessário adicionar uma foto do responsável.');
      return;
    }

    setSubmitError(null);

    try {
      await submitInvite({
        token,
        data: {
          name: data.name,
          cpf: data.cpf.replace(ONLY_DIGITS_REGEX, ''),
          phone: data.phone ? data.phone.replace(ONLY_DIGITS_REGEX, '') : undefined,
          email: data.email,
          file: photo,
        },
      });
      setIsSubmitted(true);
    } catch (error: any) {
      setSubmitError(error?.message ?? 'Não foi possível enviar o cadastro. Tente novamente.');
    }
  };

  if (isLoading) {
    return (
      <PageShell>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      </PageShell>
    );
  }

  if (isError || !inviteInfo || !inviteInfo.isValid) {
    return (
      <PageShell>
        <Typography variant="h6" gutterBottom>
          Link inválido ou expirado
        </Typography>
        <Typography>
          Este link de cadastro não é mais válido. Entre em contato com a escola para solicitar um
          novo convite.
        </Typography>
      </PageShell>
    );
  }

  if (isSubmitted) {
    return (
      <PageShell>
        <Box sx={{ textAlign: 'center', py: 2 }}>
          <CheckCircle color="success" sx={{ fontSize: 56, mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            Cadastro enviado com sucesso!
          </Typography>
          <Typography>
            Aguarde a aprovação da escola. Você será liberado assim que o cadastro for aprovado.
          </Typography>
        </Box>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <Typography variant="h6" gutterBottom>
        Cadastro de Responsável
      </Typography>
      <Typography sx={{ mb: 3 }}>
        Preencha seus dados para vincular seu acesso a: {inviteInfo.studentNames.join(', ')}
      </Typography>

      <Grid2 container direction={'column'} spacing={1}>
        <Controller
          name="name"
          control={control}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <TextFieldComponent
              label="Nome completo *"
              onChangeText={onChange}
              type="text"
              errorMessage={error?.message}
              value={value}
            />
          )}
        />

        <Controller
          name="cpf"
          control={control}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <TextFieldComponent
              label="CPF *"
              onChangeText={onChange}
              type="text"
              errorMessage={error?.message}
              value={value}
              mask="000.000.000-00"
            />
          )}
        />

        <Controller
          name="phone"
          control={control}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <TextFieldComponent
              label="Telefone"
              isOptional
              onChangeText={onChange}
              type="text"
              errorMessage={error?.message}
              value={value}
              mask="(00) 0 0000-0000"
              maxLength={16}
            />
          )}
        />

        <Controller
          name="email"
          control={control}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <TextFieldComponent
              label="E-mail *"
              onChangeText={onChange}
              type="text"
              errorMessage={error?.message}
              value={value}
            />
          )}
        />
      </Grid2>

      <Box sx={{ mt: 2 }}>
        {photo ? (
          <Box sx={{ textAlign: 'center' }}>
            <img
              src={photoPreviewUrl}
              alt="Foto do responsável"
              style={{ width: 160, height: 160, objectFit: 'cover', borderRadius: 8 }}
            />
            <ButtonComponent
              fullWidth
              color="warning"
              startIcon={<AddAPhoto />}
              onClick={() => setIsCameraOpen(true)}
              sx={{ mt: 1 }}
            >
              Trocar foto
            </ButtonComponent>
          </Box>
        ) : (
          <ButtonComponent
            fullWidth
            startIcon={<AddAPhoto />}
            onClick={() => setIsCameraOpen(true)}
          >
            Adicionar foto *
          </ButtonComponent>
        )}
        {photoError && (
          <Typography color="error" fontSize={13} sx={{ mt: 1 }}>
            {photoError}
          </Typography>
        )}
      </Box>

      {submitError && (
        <Typography color="error" sx={{ mt: 2 }}>
          {submitError}
        </Typography>
      )}

      <ButtonComponent
        variant="contained"
        color="primary"
        loading={isSubmitting}
        fullWidth
        sx={{ mt: 3 }}
        onClick={handleSubmit(handleSave)}
      >
        Enviar cadastro
      </ButtonComponent>

      <ModalComponent
        isOpen={isCameraOpen}
        title="Foto do responsável"
        cardContainerStyle={{ width: 480 }}
        onClose={() => setIsCameraOpen(false)}
      >
        <WebCamCaptureComponent
          onCapture={onCapture}
          onChangeSelectedImage={onChangeSelectedImage}
          handleCancelEdition={() => setIsCameraOpen(false)}
        />
      </ModalComponent>
    </PageShell>
  );
};

export default ResponsibleInvitePage;
